<template>
  <section class="stats-section">
    <div class="container">
      <h2 class="section-title">
        {{ $t('home.platformAchievements') }}
      </h2>
      <div class="stats-container">
        <div
          v-for="(stat, index) in stats"
          :key="index"
          class="stat-item"
          :style="{ animationDelay: `${index * 0.1}s` }"
        >
          <div class="stat-number">
            {{ stat.value }}+
          </div>
          <div class="stat-label">
            {{ stat.label }}
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { ref, onMounted, watchEffect, watch } from 'vue'
import { StatItem } from '@/api/base'
import { useI18n } from 'vue-i18n'
import { queryAchievementData } from '@/api/home'
export default {
  name: 'StatsSection',
  setup () {
    const { t } = useI18n()
    const stats = ref<{[key: string]: string | number}[]>([])
    watchEffect(() => {
      stats.value = [
        {
          id: 1,
          value: '0',
          key: 'totalIdentifiedProducts',
          label: t('home.verifiedProducts'), // '累计鉴定商品',
          targetValue: 5000000
        },
        {
          id: 2,
          value: '0',
          key: 'totalComplaintsHandled',
          label: t('home.handledComplaints'), // '处理投诉起数',
          targetValue: 20000
        },
        {
          id: 3,
          value: '0',
          key: 'totalPartnerBrands',
          label: t('home.partnerBrands'), // '合作品牌',
          targetValue: 100
        },
        {
          id: 4,
          value: '0%',
          key: 'userSatisfactionRate',
          label: t('home.userSatisfaction'), // '用户满意度',
          targetValue: 99.2
        }
      ]
    })
    const startCounting = () => {
      stats.value.forEach((stat, index) => {
        if (stat.targetValue !== undefined) {
          let current = 0
          const target = stat.targetValue as number
          const duration = 2000 // 动画持续时间(毫秒)
          const steps = 60 // 动画步数
          const increment = target / steps
          const interval = duration / steps

          const timer = setInterval(() => {
            current += increment
            if (current >= target) {
              current = target
              clearInterval(timer)
            }

            // 更新值
            if (index === 3) {
              // 对于百分比，保留一位小数
              stats.value[index].value = `${current.toFixed(1)}%`
            } else if (target > 1000) {
              // 对于大数字，添加千位分隔符
              stats.value[index].value = Math.round(current).toLocaleString() + '+'
            } else {
              stats.value[index].value = Math.round(current).toString()
            }
          }, interval)
        }
      })
    }

    const getAchievementData = () => {
      queryAchievementData().then(res => {
        console.log(res, typeof res)
        stats.value.forEach((stat) => {
          // if (stat.key && res[stat?.key]) {
          //   stat.targetValue = res[stat.key]
          // }
          switch (stat.key) {
            case 'totalIdentifiedProducts':
              stat.targetValue = res.totalIdentifiedProducts
              break
            case 'totalComplaintsHandled':
              stat.targetValue = res.totalComplaintsHandled
              break
            case 'totalPartnerBrands':
              stat.targetValue = res.totalPartnerBrands
              break
            case 'userSatisfactionRate':
              stat.targetValue = res.userSatisfactionRate
              break
          }
        })
        console.log('new', stats.value)
        updateUI()
      })
    }

    const updateUI = () => {
      // 使用Intersection Observer API来检测元素是否进入视口
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              startCounting()
              observer.unobserve(entry.target)
            }
          })
        },
        { threshold: 0.5 }
      )

      // 观察stats-section元素
      const statsSection = document.querySelector('.stats-section')
      if (statsSection) {
        observer.observe(statsSection)
      }
    }

    const { locale } = useI18n()
    watch(locale, () => {
      getAchievementData()
    })
    onMounted(() => {
      getAchievementData()
    })
    return {
      stats
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.stats-section {
  padding: $spacing-xxl 0;
  background-color: $primary-blue;
  color: $white;
  border-radius: $border-radius-xl;
  //margin: $spacing-xxl $spacing-xxl;
}

.section-title {
  text-align: center;
  font-size: $font-size-xxl;
  font-weight: 700;
  margin-bottom: $spacing-xxl;
  color: $white;
}

.stats-container {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: $spacing-xl;
}

.stat-item {
  text-align: center;
  opacity: 0;
  transform: translateY(20px);
  animation: fadeInUp 0.8s ease forwards;
}

.stat-number {
  font-size: $font-size-xxxl;
  font-weight: 700;
  color: $accent-gold;
  margin-bottom: $spacing-sm;
}

.stat-label {
  font-size: $font-size-lg;
  opacity: 0.9;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// 1080×1920 设备适配
@media (min-width: 1080px) and (min-height: 1920px) {
  .stats-container {
    flex-direction: column;
    gap: $spacing-xxl;
  }

  .stat-number {
    font-size: 3.5rem;
  }

  .stat-label {
    font-size: $font-size-xl;
  }
}

// 平板设备
@media (max-width: $breakpoint-lg) {
  .stat-number {
    font-size: $font-size-xxl;
  }

  .stat-label {
    font-size: $font-size-md;
  }
}

// 移动端适配
@media (max-width: $breakpoint-md) {
  .stats-container {
    flex-direction: column;
    gap: $spacing-xl;
  }

  .section-title {
    font-size: $font-size-xl;
    margin-bottom: $spacing-xl;
  }

  .stat-number {
    font-size: $font-size-xl;
  }
}
</style>
